import { useCallback, useMemo } from 'react';

import { useActiveAbTestForWorkflow } from 'src/hooks/useActiveAbTestForWorkflow';
import { useCurrentWorkflowAccess } from 'src/hooks/useChannelAccess';
import { useSelectedAutoflowVersion } from 'src/hooks/useSelectedAutoflowVersion';
import { useAutopilotConfig } from 'src/pages/workflow-builder-edit/email-builder-page/intent-email-builder/components/autopilot/useAutopilotConfig';
import { useGetBuilderQueryParams } from 'src/pages/workflow-builder-edit/hooks';
import { variantLookupKey } from 'src/pages/workflow-builder-edit/utils/buildVariantLookup';
import { ABTestVariantInfo } from 'src/services/insights/types';
import {
  useGetAutoflowWorkflowQuery,
  useGetAutoflowWorkflowVersionQuery,
} from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { AutoflowIntentWorkflowResponse } from 'src/types/workflowBuilderAPITypes';

interface UseAutoflowVersionManagerReturn {
  activeAbTest: ABTestVariantInfo | undefined;
  hasAbTest: boolean;
  isWorkflowInActiveAbTest: boolean;
  testedVersions: Set<string>;
  /** Always has version data - either selected version or current workflow */
  currentOrVersionData: AutoflowIntentWorkflowResponse | undefined;
  /**
   * Like `currentOrVersionData` but `currentData`-backed (non-sticky):
   * undefined while the cached value belongs to a previous workflow/version
   * selection. Use when acting on the data would be wrong during a switch.
   */
  currentOrVersionFreshData: AutoflowIntentWorkflowResponse | undefined;
  isLoadingVersion: boolean;
  isReadOnly: boolean;
  onClearSelectedVersion: () => void;
  onSelectVersion: (version: number, versionType?: WorkflowVersionType) => void;
  refetchVersion: () => void;
  selectedVersion: {
    version: number;
    versionType?: WorkflowVersionType;
  } | null;
  /** Only has data when a specific version is selected (selectedVersion !== null) */
  versionData: AutoflowIntentWorkflowResponse | undefined;
}

export const useAutoflowVersionManager =
  (): UseAutoflowVersionManagerReturn => {
    const { workflowId: intentWorkflowId } = useGetBuilderQueryParams();
    const { clearSelectedVersion, selectedVersion, setSelectedVersion } =
      useSelectedAutoflowVersion();
    const abTestInfo = useActiveAbTestForWorkflow(intentWorkflowId);
    // In the email-autopilot modal, the URL workflowId points at the email
    // workflow — not the autopilot parent — so its is_published / version_type
    // values are not meaningful for editing the autopilot building block.
    const { configurationId } = useAutopilotConfig();
    const isAutopilotContext = Boolean(configurationId);

    const {
      currentData: versionCurrentData,
      data: versionData,
      isLoading: isLoadingVersion,
      refetch: refetchVersion,
    } = useGetAutoflowWorkflowVersionQuery(
      {
        intentWorkflowId,
        version: selectedVersion?.version ?? 0,
        versionType: selectedVersion?.versionType,
      },
      {
        skip: selectedVersion === null || !intentWorkflowId,
      },
    );

    const { currentData: workflowCurrentData, data: workflowData } =
      useGetAutoflowWorkflowQuery(intentWorkflowId, {
        skip: !intentWorkflowId,
      });

    const onSelectVersion = (
      version: number,
      versionType?: WorkflowVersionType,
    ) => {
      setSelectedVersion(version, versionType);
    };

    // Stable identity: consumers hang this off memoized event handlers.
    const onClearSelectedVersion = useCallback(() => {
      clearSelectedVersion();
    }, [clearSelectedVersion]);

    // Determine read-only status:
    // - When viewing a specific version: read-only if version_type === 'published'
    //   or if the version is part of an active AB test
    //   (also read-only while loading to prevent brief editable flash)
    // - When viewing current (selectedVersion === null): read-only if workflow is
    //   published (is_published === true), which also covers the live version being
    //   part of an active AB test
    const { canEdit } = useCurrentWorkflowAccess();
    const isReadOnly = useMemo(() => {
      if (!canEdit) {
        return true;
      }
      // Autopilot building blocks are edited inside AutopilotPolicyView, which
      // does not surface a published/historic distinction — defer to that flow.
      if (isAutopilotContext) {
        return false;
      }
      if (selectedVersion !== null) {
        if (isLoadingVersion) {
          return true;
        } // Stay read-only while loading
        if (versionData?.version_type === 'published') {
          return true;
        }
        // Lock versions that are part of an active AB test
        if (
          abTestInfo.isWorkflowInActiveAbTest &&
          versionData?.version !== undefined &&
          versionData?.version_type &&
          abTestInfo.testedVersions.has(
            variantLookupKey(versionData.version, versionData.version_type),
          )
        ) {
          return true;
        }
        return false;
      }
      // Current/live view: read-only if published (live version cannot be
      // modified while it may be part of an active test)
      return workflowData?.is_published === true;
    }, [
      canEdit,
      isAutopilotContext,
      selectedVersion,
      isLoadingVersion,
      versionData?.version_type,
      versionData?.version,
      abTestInfo.isWorkflowInActiveAbTest,
      abTestInfo.testedVersions,
      workflowData?.is_published,
    ]);

    return {
      ...abTestInfo,
      currentOrVersionData:
        selectedVersion !== null ? versionData : workflowData,
      currentOrVersionFreshData:
        selectedVersion !== null ? versionCurrentData : workflowCurrentData,
      isLoadingVersion,
      isReadOnly,
      onClearSelectedVersion,
      onSelectVersion,
      refetchVersion,
      selectedVersion,
      versionData: selectedVersion !== null ? versionData : undefined,
    };
  };
